import "App.css";
import { Routes, Route, Navigate } from "react-router-dom";

import { ThemeProvider } from "@fluentui/react";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import { APP_THEME } from "Tools/theme";
import { LINKS } from "Tools/navigation";
import { ProtectedView } from "Components/guards/ProtectedView";
import { AuthContext } from "Data/auth/context";
import { useAuthContext, useAuthProvider } from "Data/auth/hooks";
import { NoAuthView } from "Components/guards/NoAuthView";
import { lazy } from "react";
import dayjs from "dayjs";
import * as relativeTime from "dayjs/plugin/relativeTime";
import { GlobalDiallogContext } from "Data/dialog/context";
import { useGlobalialog } from "Data/dialog/hooks";
import {
  canAccessSoilSensorAdminSensors,
  soilSensorDefaultPath,
} from "Tools/user/shared";
import { LoadingResourceError } from "Components/LoadingResourceError/LoadingResourceError";
import { Loadable } from "Components/Loadable";
import { PERMISSIONS } from "Tools/permissions";
import { setWindowTitle } from "Tools/ui/media";


const MainInterface = lazy(() => import("Pages/main-interface/MainInterfaceRoot"));

const FrostRoot = lazy(() => import("Pages/main-interface/FrostRoot"));
const ForecastRoot = lazy(() => import("Pages/main-interface/frost/ForecastRoot"));
const ForecastList = lazy(() => import("Pages/main-interface/frost/forecast/ForecastList"));
const ForecastAdd = lazy(() => import("Pages/main-interface/frost/forecast/ForecastAdd"));
const ReservationRoot = lazy(() => import("Pages/main-interface/frost/ReservationRoot"));
const ReservationReport = lazy(() => import("Pages/main-interface/frost/reservation/ReservationReport"));
const FROSTOrderingRoot = lazy(() => import("Pages/main-interface/frost/OrderingRoot"));
const FROSTOrderingSampleManagement = lazy(() => import("Pages/main-interface/frost/ordering/SampleManagement"));
const FROSTOrderingCreate = lazy(() => import("Pages/main-interface/frost/ordering/OrderingCreate"));
const FROSTOrderingReport = lazy(() => import("Pages/main-interface/frost/ordering/OrderingReport"));
const FROSTOrderingAssign = lazy(() => import("Pages/main-interface/frost/ordering/OrderingAssign"));
const FROSTTDManagement = lazy(() => import('Pages/main-interface/frost/ordering/TDManagement'))
const FROSTShippingRoot = lazy(() => import("Pages/main-interface/frost/ShippingRoot"));
const FROSTShippingReport = lazy(() => import("Pages/main-interface/frost/shipping/ShippingReport"));
const TrackingRoot = lazy(() => import("Pages/main-interface/frost/TrackingRoot"));
const TrackingGEPReport = lazy(() => import("Pages/main-interface/frost/tracking/Report"));
const FROSTOrderingDataFlags = lazy(() => import("Pages/main-interface/frost/ordering/FlaggedData"));

const ChemicalsStockRoot = lazy(() => import("Pages/main-interface/ChemicalsStockRoot"));
const EquipmentStockRoot = lazy(() => import("Pages/main-interface/EquipmentStockRoot"));
const AddChemicalProduct = lazy(() => import("Pages/main-interface/chemicals-stock/AddChemicalProduct"));
const ViewChemicalProducts = lazy(() => import("Pages/main-interface/chemicals-stock/ViewChemicalProducts"));
const ChemnicalProductOrders = lazy(() => import("Pages/main-interface/chemicals-stock/order-management/OrderManagement"));
const ChemicalBranchManagementRoot = lazy(() => import("Pages/main-interface/chemicals-stock/BranchManagementRoot"));
const ChemicalBranches = lazy(() => import("Pages/main-interface/chemicals-stock/branch-management/Branches"));
const ChemicalsTrackingGEPReport = lazy(() => import("Pages/main-interface/chemicals-stock/tracking/TrackingManagement"));
const ChemicalsTrackingRoot = lazy(() => import("Pages/main-interface/chemicals-stock/TrackingRoot"));
const ChemicalBranchesMembers = lazy(() => import("Pages/main-interface/chemicals-stock/branch-management/AssignedMembers"));


const SettingsRoot = lazy(() => import("Pages/main-interface/SettingsRoot"));
const ApplicationSettingsRoot = lazy(() => import("Pages/main-interface/settings/ApplicationSettingsRoot"));
const DeveloperConsoleSettings = lazy(() => import("Pages/main-interface/settings/application/DeveloperConsole/DeveloperConsole"));
const AuditTrailPage = lazy(() => import("Pages/main-interface/audit-trail/AuditTrailPage"));
const SystemMetricsRoot = lazy(() => import("Pages/main-interface/system-metrics/SystemMetricsRoot"));
const SystemMetricsPage = lazy(() => import("Pages/main-interface/system-metrics/SystemMetricsPage"));
const FROSTSettings = lazy(() => import("Pages/main-interface/settings/application/FROSTSettings"));
const MyAccountSettings = lazy(() => import("Pages/main-interface/settings/MyAccount"));
const EquipmentStockSettings = lazy(() => import("Pages/main-interface/settings/application/EquipmentStockSettings"));
const SeedcareSettings = lazy(() => import("Pages/main-interface/settings/application/SeedcareSettings"));
const ResistanceSamplingSettings = lazy(() => import("Pages/main-interface/settings/application/ResistanceSamplingSettings"));
const ChemicalStockSettings = lazy(() => import("Pages/main-interface/settings/application/ChemicalsStockSettings"));
const DQRTSettings = lazy(() => import("Pages/main-interface/settings/application/DQRTSettings"));
const ApplCalculatorSettings = lazy(() => import("Pages/main-interface/settings/application/ApplCalculatorSettings"));


const SamplingRoot = lazy(() => import("Pages/main-interface/Sampling"));




const DeviceDemoGraphics = lazy(() => import("Pages/main-interface/equipment-stock/DeviceApprovalManagement"));
const DeviceAchieves = lazy(() => import("Pages/main-interface/equipment-stock/DeviceArchives"));
const AssignedDevices = lazy(() => import("Pages/main-interface/equipment-stock/devices/AssignedDevices"));
const DeviceRequests = lazy(() => import("Pages/main-interface/equipment-stock/devices/DeviceRequests"));
const DeviceMasterList = lazy(() => import("Pages/main-interface/equipment-stock/devices/DeviceMasterList"));
const Devices = lazy(() => import("Pages/main-interface/equipment-stock/Devices"));
const AllDevices = lazy(() => import("Pages/main-interface/equipment-stock/devices/AllDevices"));
const ReportManager = lazy(() => import("Pages/main-interface/equipment-stock/report-manager/ReportManager"));


const SeedcareRoot = lazy(() => import("Pages/main-interface/SeedCareRoot"));
const SeedcareSeedTreatmentRoot = lazy(() => import("Pages/main-interface/seedcare/SeedTreatmentRoot"));
// const SeedcareSeedTreatmentRequest = lazy(() => import("Pages/main-interface/seedcare/seed-treatment/Request"));
const SeedcareSeedTreatmentRequestOverview = lazy(() => import("Pages/main-interface/seedcare/seed-treatment/RequestOverview/RequestOverview"));
const SeedcareSeedTreatmentSTFProtocols = lazy(() => import("Pages/main-interface/seedcare/seed-treatment/STFProtocolsPerTrial"));
const SeedcareSeedTreatmentRTMTrial = lazy(() => import("Pages/main-interface/seedcare/seed-treatment/RTMRequestsTrial"));
const SeedcareSeedTreatmentBlockedDemandIDs = lazy(() => import("Pages/main-interface/seedcare/seed-treatment/BlockedDemandIDs/BlockedDemandIDs"));
const SeedcareSeedTreatmentSTFShipmentSheet = lazy(() => import("Pages/main-interface/seedcare/seed-treatment/STFShipmentSheet"));
const SeedcareSeedTreatmentRequestList = lazy(() => import("Pages/main-interface/seedcare/seed-treatment/AssignmentsList"));
const SeedcareSeedTreatmentPlan = lazy(() => import("Pages/main-interface/seedcare/seed-treatment/TreatmentPlan/TreatmentPlan"));
const SeedcareProductAssessment=lazy(()=>import("Pages/main-interface/seedcare/seed-treatment/ProductAssessment/ProductAssessment"))
const SeedcareInfectedSeedsDBRoot = lazy(() => import('Pages/main-interface/seedcare/InfectedSeedsDatabaseRoot'))
const SeedcareInfectedSeedsDBManagement = lazy(() => import('Pages/main-interface/seedcare/infected-seeds-database/Management'))

const SoilSensorRoot = lazy(() => import("Pages/main-interface/SoilSensorRoot"));
const ProtocolOwnerView = lazy(() => import("Pages/main-interface/soil-sensor/protocol-owner/ProtocolOwnerView"));
const SoilSensorCreateOrderPage = lazy(() => import("Pages/main-interface/soil-sensor/create-order/SoilSensorCreateOrderPage"));
const SoilSensorProvidersPage = lazy(() => import("Pages/main-interface/soil-sensor/providers/SoilSensorProvidersPage"));
const AdminSensorsPage = lazy(() => import("Pages/main-interface/soil-sensor/admin-sensors/AdminSensorsPage"));

const PeopleRoot = lazy(() => import("Pages/main-interface/People"));
const Index = lazy(() => import("Pages/main-interface/Index/Index"));
const Login = lazy(() => import("Pages/auth/Login"));
const SSOSignIn = lazy(() => import("Pages/auth/SSOSignIn"));
const AccountRecovery = lazy(() => import("Pages/auth/AccountRecovery"));

const UtilitiesRoot = lazy(() => import("Pages/main-interface/StatistictRoot"));
const ApplCalculatorRoutes = lazy(() => import("Pages/main-interface/appl-calculator"));
const DataQualityRoot = lazy(() => import("Pages/main-interface/utilities/DataQualityRoot"));
const DataQualityAuditTable = lazy(() => import("Pages/main-interface/utilities/data-quality/AuditTable"));
const DataQualityRuleBook = lazy(() => import("Pages/main-interface/utilities/data-quality/RuleBook"));
const DataQualitySearchPage = lazy(() => import("Pages/main-interface/utilities/data-quality/SearchPage/SearchPage"));
const DataQualityOverview = lazy(() => import("Pages/main-interface/utilities/data-quality/Overview/Overview"));
const DataQualityReview = lazy(() => import("Pages/main-interface/utilities/data-quality/Review"));


const UtilityIdentificationRoot = lazy(() => import("Pages/main-interface/utilities/UtilityIdentificationRoot"));
const UtilityIdentificationBulkOperations = lazy(() => import("Pages/main-interface/utilities/utility-identification/BulkOperations"));
const UtilityIdentificationBulkResistanceSampling = lazy(() => import("Pages/main-interface/utilities/utility-identification/ResistanceSampling"));
const UtilityIdentificationValencoDevices = lazy(() => import("Pages/main-interface/utilities/valenco/ValencoDevices"));


const ActivitiesRoot = lazy(() => import("Pages/main-interface/ActivitiesRoot"));
const ActivitiesPlanningRoot = lazy(() => import("Pages/main-interface/activity-center/PlanningRoot"));
const ActivitiesPlanningCalendar = lazy(() => import("Pages/main-interface/activity-center/Planning/PlanningCalendar/PlanningCalendar"));
const ActivitiesPlanningOverview = lazy(() => import("Pages/main-interface/activity-center/Planning/Overview/Overview"));

initializeIcons();



dayjs.extend(relativeTime.default);
setWindowTitle()


const AdminSensorsGate = ({ children }: { children: JSX.Element }) => {
  const { user } = useAuthContext()
  return canAccessSoilSensorAdminSensors(user)
    ? children
    : <LoadingResourceError retry={false} />
}

const SoilSensorIndexRedirect = () => {
  const { user } = useAuthContext()
  return <Navigate to={soilSensorDefaultPath(user)} replace />
}

function App() {
  const { user, setUser, loading, signIn, signOut, switchToAccount, hasAccountSwitcherToken } = useAuthProvider();
  const gNotif = useGlobalialog();

  return (
    <ThemeProvider theme={APP_THEME}>
      <GlobalDiallogContext.Provider value={gNotif}>
        <AuthContext.Provider value={{ user, setUser, loading, signIn, signOut, switchToAccount, hasAccountSwitcherToken }}>
          <Routes>
            <Route element={<Loadable><ProtectedView><MainInterface /></ProtectedView></Loadable>} path={LINKS.home}>

              <Route element={<Loadable permissions={PERMISSIONS.FROST.PERMISSIONS}><FrostRoot /></Loadable>} path={LINKS.$frost}>
                {/* Frost forecast routes */}
                <Route element={<Loadable  {...{ anyPermissions: PERMISSIONS.FROST.Forecast.ANY_PERMISSIONS }}><ForecastRoot /></Loadable>} path={LINKS.frost.$forecast}>
                  <Route element={<Loadable><ForecastList /></Loadable>} path={LINKS.frost.forecast.$list} />
                  <Route element={<Loadable  {...{ anyPermissions: PERMISSIONS.FROST.Forecast.Add.ANY_PERMISSIONS }}><ForecastAdd /></Loadable>} path={LINKS.frost.forecast.$add} />
                </Route>

                {/* Frost reservation routes */}
                <Route element={<Loadable anyPermissions={PERMISSIONS.FROST.Reservation.ANY_PERMISSIONS}><ReservationRoot /></Loadable>} path={LINKS.frost.$reservation}>
                  <Route element={<Loadable><ReservationReport /></Loadable>} path={LINKS.frost.reservation.$report} />
                  <Route element={<Loadable anyPermissions={PERMISSIONS.FROST.Reservation.SampleManagement.ANY_PERMISSIONS}><FROSTOrderingSampleManagement /></Loadable>} path={LINKS.frost.reservation.$sampleManagement} />
                </Route>

                <Route element={<Loadable anyPermissions={PERMISSIONS.FROST.Ordering.ANY_PERMISSIONS}><FROSTOrderingRoot /></Loadable>} path={LINKS.frost.$ordering} >
                  <Route element={<Loadable anyPermissions={PERMISSIONS.FROST.Ordering.Assign.ANY_PERMISSIONS}><FROSTOrderingAssign /></Loadable>} path={LINKS.frost.ordering.$assign} />
                  <Route element={<Loadable anyPermissions={PERMISSIONS.FROST.Ordering.Create.ANY_PERMISSIONS}><FROSTOrderingCreate /></Loadable>} path={LINKS.frost.ordering.$create} />
                  <Route element={<Loadable anyPermissions={PERMISSIONS.FROST.Ordering.Report.ANY_PERMISSIONS}><FROSTOrderingReport /></Loadable>} path={LINKS.frost.ordering.$report} />
                  <Route element={<Loadable anyPermissions={PERMISSIONS.FROST.Ordering.TDManagement.ANY_PERMISSIONS}><FROSTTDManagement /></Loadable>} path={LINKS.frost.ordering.$tdmanagement} />
                  <Route element={<Loadable permissions={PERMISSIONS.FROST.Ordering.FlaggedData.PERMISSIONS}><FROSTOrderingDataFlags /></Loadable>} path={LINKS.frost.ordering.$flaggedData} />
                </Route>

                <Route element={<Loadable anyPermissions={PERMISSIONS.FROST.Tracking.ANY_PERMISSIONS}><TrackingRoot /></Loadable>} path={LINKS.frost.$tracking} >
                  <Route element={<Loadable><TrackingGEPReport /></Loadable>} path={LINKS.frost.tracking.report} />
                </Route>

                <Route element={<Loadable><FROSTShippingRoot /></Loadable>} path={LINKS.frost.$shipping}>
                  <Route element={<Loadable><FROSTShippingReport /></Loadable>} path={LINKS.frost.shipping.$report} />
                </Route>
              </Route>


              <Route element={<Loadable {...{ permissions: PERMISSIONS.Chemicals.PERMISSIONS }}><ChemicalsStockRoot /></Loadable>} path={LINKS.$chemicalStock}>
                <Route element={<Loadable {...{ permissions: PERMISSIONS.Chemicals.Products.Add.PERMISSIONS }} ><AddChemicalProduct /></Loadable>} path={LINKS.chemicalStock.$addProduct} />
                <Route element={<Loadable><ViewChemicalProducts /></Loadable>} path={LINKS.chemicalStock.$viewProducts} />
                <Route element={<Loadable {...{ anyPermissions: PERMISSIONS.Chemicals.Tracking.ANY_PERMISSIONS }}  ><ChemicalsTrackingRoot /></Loadable>} path={LINKS.chemicalStock.$tracking}>
                  <Route element={<Loadable><ChemicalsTrackingGEPReport /></Loadable>} path={LINKS.chemicalStock.tracking.gepReport} />
                </Route>
                <Route element={<Loadable><ChemnicalProductOrders /></Loadable>} path={LINKS.chemicalStock.$productOrders} />
                <Route element={<Loadable {...{ anyPermissions: PERMISSIONS.Chemicals.Branches.ANY_PERMISSIONS }} ><ChemicalBranchManagementRoot /></Loadable>} path={LINKS.chemicalStock.$branches}>
                  <Route element={<Loadable  {...{ permissions: PERMISSIONS.Chemicals.Branches.List.PERMISSIONS }}><ChemicalBranches /></Loadable>} path={LINKS.chemicalStock.branches.$list} />
                  <Route element={<Loadable><ChemicalBranchesMembers /></Loadable>} path={LINKS.chemicalStock.branches.$members} />
                </Route>
              </Route>

              {/* Equipment Stock Routes */}
              <Route element={<Loadable><EquipmentStockRoot /></Loadable>} path={LINKS.$equipmentStock}>
                <Route element={<Loadable><Devices /></Loadable>} path={LINKS.equipmentStock.$devices}>
                  <Route element={<Loadable><AllDevices /></Loadable>} path={LINKS.equipmentStock.devices.$allDevices} />
                  <Route element={<Loadable {...{ anyPermissions: PERMISSIONS.Equipment.ANY_APPROVE_REJECT_REQUEST }}><DeviceRequests /></Loadable>} path={LINKS.equipmentStock.devices.$deviceRequests} />
                  <Route element={<Loadable><AssignedDevices /></Loadable>} path={LINKS.equipmentStock.devices.$assignedDevices} />
                  <Route element={<Loadable><DeviceMasterList /></Loadable>} path={LINKS.equipmentStock.devices.$deviceMasterList} />
                </Route>
                <Route element={<Loadable><DeviceAchieves /></Loadable>} path={LINKS.equipmentStock.$deviceUsage} />
                <Route element={<Loadable><DeviceDemoGraphics /></Loadable>} path={LINKS.equipmentStock.$deviceApproval} />
                <Route element={<Loadable {...{ anyPermissions: PERMISSIONS.Equipment.ReportManager.PERMISSIONS }}><ReportManager /></Loadable>} path={LINKS.equipmentStock.reportManager.$reportmanager} />
              </Route>


              {/* Seedcare routes */}
              <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.PERMISSIONS }}><SeedcareRoot /></Loadable>} path={LINKS.$seedcare}>
                <Route element={<Loadable /*{...{ user, anyPermissions: PERMISSIONS.Seedcare. }}*/><SeedcareSeedTreatmentRoot /></Loadable>} path={LINKS.seedCare.$seedTreatment}>
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.SeedTreatmentApp.STFProtocolsPerTrial.PERMISSIONS }}><SeedcareSeedTreatmentSTFProtocols /></Loadable>} path={LINKS.seedCare.seedTreatment.$stfProtocolsPerTrial} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.SeedTreatmentApp.TreatmentRequest.RequestOverview.PERMISSIONS }}><SeedcareSeedTreatmentRequestOverview /></Loadable>} path={LINKS.seedCare.seedTreatment.$requestOverview} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.SeedTreatmentApp.TreatmentRequest.RTMRequestTrial.PERMISSIONS }}><SeedcareSeedTreatmentRTMTrial /></Loadable>} path={LINKS.seedCare.seedTreatment.$rtmRequestTrial} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.SeedTreatmentApp.AssignedTrials.PERMISSIONS }}><SeedcareSeedTreatmentRequestList /></Loadable>} path={LINKS.seedCare.seedTreatment.$assignmentsList} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.TreatmentPlan.PERMISSIONS }}><SeedcareSeedTreatmentPlan /></Loadable>} path={LINKS.seedCare.seedTreatment.$treatmentPlan} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.ProductAssessment.PERMISSIONS }}><SeedcareProductAssessment /></Loadable>} path={LINKS.seedCare.seedTreatment.$productAssessment} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.SeedTreatmentApp.STFShipping.PERMISSIONS }}><SeedcareSeedTreatmentSTFShipmentSheet /></Loadable>} path={LINKS.seedCare.seedTreatment.$stfShipping} />
                  <Route element={<Loadable  {...{ permissions: PERMISSIONS.user.ADMIN_SEEDCARE }} ><SeedcareSeedTreatmentBlockedDemandIDs /></Loadable>} path={LINKS.seedCare.seedTreatment.$blockedDemandIDs} />
                </Route>
                <Route element={<Loadable {...{ permissions: PERMISSIONS.Seedcare.InfectedSeedsApp.ANY_PERMISSIONS }} ><SeedcareInfectedSeedsDBRoot /></Loadable>} path={LINKS.seedCare.$infectedSeedsDatabase}>
                  <Route element={<Loadable ><SeedcareInfectedSeedsDBManagement /></Loadable>} path={LINKS.seedCare.infectedSeedsDatabase.$management} />
                </Route>

                {/* <Route element={<Loadable {...{  anyPermissions: PERMISSIONS.Seedcare.UPSShipping.ANY_PERMISSIONS }}><SeedcareUPSShippingRoot /></Loadable>} path={LINKS.seedCare.$upsShipping}>
                  <Route element={<Loadable {...{  anyPermissions: PERMISSIONS.Seedcare.UPSShipping.ANY_PERMISSIONS }}><SeedcareCreateUPSShipment /></Loadable>} path={LINKS.seedCare.upsShipping.$create} />
                </Route> */}
              </Route>



              {/* Sampling */}
              <Route  element={<Loadable {...{ permissions: PERMISSIONS.ResistanceSampling.PERMISSIONS }}><SamplingRoot/></Loadable>}
                path={LINKS.$resistanceSampling+'/*'}/>

              <Route element={<Loadable anyPermissions={PERMISSIONS.SoilSensor.ANY_PERMISSIONS}><SoilSensorRoot /></Loadable>} path={LINKS.$soilSensor}>
                <Route
                  index
                  element={<SoilSensorIndexRedirect />}
                />
                <Route
                  path={LINKS.$soilSensorProtocolOwner}
                  element={
                    <Loadable anyPermissions={PERMISSIONS.SoilSensor.ProtocolRequests.ANY_PERMISSIONS}>
                      <ProtocolOwnerView />
                    </Loadable>
                  }
                />
                <Route
                  path={LINKS.$soilSensorCreateOrder}
                  element={
                    <Loadable anyPermissions={PERMISSIONS.SoilSensor.CreateOrder.ANY_PERMISSIONS}>
                      <SoilSensorCreateOrderPage />
                    </Loadable>
                  }
                />
                <Route
                  path={LINKS.$soilSensorShippingModule}
                  element={<Navigate to={`${LINKS.soilSensorCreateOrder}?tab=shipping`} replace />}
                />
                <Route
                  path={LINKS.$soilSensorTracking}
                  element={<Navigate to={`${LINKS.soilSensorProviders}?tab=tracking`} replace />}
                />
                <Route
                  path={LINKS.$soilSensorProviders}
                  element={
                    <Loadable anyPermissions={PERMISSIONS.SoilSensor.Providers.ANY_PERMISSIONS}>
                      <SoilSensorProvidersPage />
                    </Loadable>
                  }
                />
                <Route
                  path={LINKS.$soilSensorAdminSensors}
                  element={
                    <AdminSensorsGate>
                      <AdminSensorsPage />
                    </AdminSensorsGate>
                  }
                />
              </Route>

              {/* TAQOS+ (data quality) routes */}
              <Route element={<Loadable permissions={PERMISSIONS.ApplCalculator.PERMISSIONS}><ApplCalculatorRoutes /></Loadable>} path={LINKS.$applCalculator + '/*'} />
              <Route element={<Loadable permissions={PERMISSIONS.Utilities.DataQuality.PERMISSIONS}><DataQualityRoot /></Loadable>} path={LINKS.$dataQuality}>
                <Route element={<Loadable permissions={PERMISSIONS.Utilities.DataQuality.Audit.PERMISSIONS}><DataQualityAuditTable /></Loadable>} path={LINKS.dataQuality.$auditTable} />
                <Route element={<Loadable permissions={PERMISSIONS.Utilities.DataQuality.RuleBook.PERMISSIONS}><DataQualityRuleBook /></Loadable>} path={LINKS.dataQuality.$ruleBook} />
                <Route element={<Loadable permissions={PERMISSIONS.Utilities.DataQuality.Search.PERMISSIONS}><DataQualitySearchPage /></Loadable>} path={LINKS.dataQuality.$search} />
                <Route element={<Loadable permissions={PERMISSIONS.Utilities.DataQuality.Overview.PERMISSIONS}><DataQualityOverview /></Loadable>} path={LINKS.dataQuality.$overview} />
                <Route element={<Loadable permissions={PERMISSIONS.Utilities.DataQuality.Review.PERMISSIONS}><DataQualityReview /></Loadable>} path={LINKS.dataQuality.$review} />
              </Route>


              {/* Utilities routes */}
              <Route element={<Loadable permissions={PERMISSIONS.Utilities.PERMISSIONS}><UtilitiesRoot /></Loadable>} path={LINKS.$utilities}>
                <Route element={<Loadable permissions={PERMISSIONS.Equipment.Valenco.PERMISSIONS}><UtilityIdentificationValencoDevices /></Loadable>} path={LINKS.utilities.$valencoDevices} />


                <Route element={<Loadable permissions={PERMISSIONS.Utilities.UtilityIdentification.PERMISSIONS}><UtilityIdentificationRoot /></Loadable>} path={LINKS.utilities.$utilityIdentification}>
                  <Route element={<Loadable permissions={PERMISSIONS.Equipment.PRINT_QR_CODE}><UtilityIdentificationBulkOperations /></Loadable>} path={LINKS.utilities.utilityIdentification.$bulkOperations} />
                  <Route element={<Loadable permissions={PERMISSIONS.ResistanceSampling.PRINT_QR_CODE}><UtilityIdentificationBulkResistanceSampling /></Loadable>} path={LINKS.utilities.utilityIdentification.$resistanceSampling} />
                </Route>



              </Route>



              <Route element={<Loadable permissions={PERMISSIONS.Activity.PERMISSIONS}><ActivitiesRoot /></Loadable>} path={LINKS.$activities}>
                <Route element={<Loadable ><ActivitiesPlanningRoot /></Loadable>} path={LINKS.activities.$planning} >
                  <Route element={<Loadable ><ActivitiesPlanningCalendar /></Loadable>} path={LINKS.activities.planning.$calendar} />
                  <Route element={<Loadable ><ActivitiesPlanningOverview /></Loadable>} path={LINKS.activities.planning.$overview} />
                </Route>
              </Route>

              <Route element={<Loadable anyPermissions={PERMISSIONS.People.PERMISSIONS}><PeopleRoot /></Loadable>} path={LINKS.$people} />

              <Route element={<Loadable permissions={PERMISSIONS.user.SYSTEM}><SystemMetricsRoot /></Loadable>} path={LINKS.$systemMetrics}>
                <Route index element={<SystemMetricsPage />} />
              </Route>

              <Route element={<Loadable><Index /></Loadable>} path="/" />

              <Route element={<Loadable anyPermissions={[]}><SettingsRoot /></Loadable>} path={LINKS.$settings}>
                <Route path={LINKS.settings.$myAccount} element={<Loadable><MyAccountSettings /></Loadable>} />
                <Route element={<Loadable permissions={PERMISSIONS.Settings.ANY_PERMISSIONS}><ApplicationSettingsRoot /></Loadable>} path={LINKS.settings.$application}>
                  <Route element={<Loadable permissions={PERMISSIONS.Settings.FROST.PERMISSIONS}><FROSTSettings /></Loadable>} path={LINKS.settings.application.$frost} />
                  <Route element={<Loadable permissions={PERMISSIONS.Settings.DigitalInv.PERMISSIONS}><EquipmentStockSettings /></Loadable>} path={LINKS.settings.application.$equipmentStock} />
                  <Route element={<Loadable permissions={PERMISSIONS.Settings.ChemicalInv.PERMISSIONS}><ChemicalStockSettings /></Loadable>} path={LINKS.settings.application.$chemicalsStock} />
                  <Route element={<Loadable permissions={PERMISSIONS.Settings.Seedcare.PERMISSIONS}><SeedcareSettings /></Loadable>} path={LINKS.settings.application.$seedcare} />
                  <Route element={<Loadable permissions={PERMISSIONS.Settings.ResistanceSampling.PERMISSIONS}><ResistanceSamplingSettings /></Loadable>} path={LINKS.settings.application.$resistanceSampling} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.Settings.DataQuality.PERMISSIONS }}><DQRTSettings /></Loadable>} path={LINKS.settings.application.$dqrt} />
                  <Route element={<Loadable permissions={PERMISSIONS.Settings.ApplCalculator.PERMISSIONS}><ApplCalculatorSettings /></Loadable>} path={LINKS.settings.application.$applCalculator} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.user.SYSTEM }} ><DeveloperConsoleSettings /></Loadable>} path={LINKS.settings.application.$developerConsole} />
                  <Route element={<Loadable {...{ permissions: PERMISSIONS.user.SYSTEM }} ><AuditTrailPage /></Loadable>} path={LINKS.settings.application.$auditTrail} />
                  <Route path="module-metrics" element={<Navigate to={LINKS.systemMetrics} replace />} />
                  <Route path="**" element={<Navigate to={'/'} />} />
                </Route>

              </Route>



            </Route>



            <Route element={<Loadable><NoAuthView><Login /></NoAuthView></Loadable>} path={LINKS.$login} />
            <Route element={<Loadable><NoAuthView><SSOSignIn /></NoAuthView></Loadable>} path={LINKS.$SSOSignIn} />
            <Route element={<Loadable><NoAuthView><AccountRecovery /></NoAuthView></Loadable>} path={LINKS.$accountRecovery} />
          </Routes>
        </AuthContext.Provider>
      </GlobalDiallogContext.Provider>
    </ThemeProvider>
  );
}

export default App;
